import Phaser from 'phaser';
import type { MapLabel } from '../mapLabels.js';
import type { ScooterSnapshot } from '../scooters.js';

/**
 * Contrat exhaustif de tous les événements entre Phaser, React, et les services.
 * Ajouter ici avant d'émettre ou d'écouter un nouvel événement.
 * Convention : "domaine:action"
 */
/** Pourquoi une session a été coupée à distance (message affiché à l'utilisateur). */
export type SessionRevokedReason = 'password-changed' | 'role-changed' | 'access-changed' | 'session-expired';

export type GameEventMap = {
  // ── Phaser → React / Services ────────────────────────────
  /** spawnX/spawnY : position réelle du joueur local dans la zone (calculée depuis le TMJ),
   *  transmise à Colyseus dès la connexion pour que le serveur n'assigne jamais une position
   *  par défaut arbitraire — sinon proximité/bulles restent aveugles à ce joueur tant qu'il
   *  n'a pas envoyé son premier player:moved. */
  'zone:entered':             { zoneId: string; zoneName: string; spawnX: number; spawnY: number };
  'zone:exited':              { zoneId: string };
  'player:nearby':            { userId: string; displayName: string; distance: number };
  'player:left-proximity':    { userId: string };
  'scene:ready':              { scene: Phaser.Scene; sceneKey: string };
  'transition:started':       { fromZoneId: string; toZoneId: string; spawnName?: string | undefined };
  'transition:complete':      { zoneId: string };
  /** Positions screen — émis 1x par frame. Ne jamais mettre dans Zustand. */
  'overlay:positions-updated': Map<string, { x: number; y: number }>;
  /** bubbleIds mis à jour par Colyseus — utilisé par LiveKitService pour les souscriptions */
  'colyseus:bubble-ids-updated': Map<string, string | null>;
  /** Liste complète des joueurs dans la zone (room:state / player:join / player:leave) */
  'colyseus:zone-players-updated': Map<string, { userId: string; displayName: string; status: string }>;
  /** Joueurs dans le rayon de proximité audio (hors bulle) — émis par Colyseus via ColyseusClient */
  'proximity:changed': { nearbyUserIds: string[] };
  /** bubbleId du joueur local mis à jour */
  'colyseus:local-bubble-changed': { bubbleId: string | null };
  /** Serveur a détecté un changement de bulle pour le joueur local (avec cooldown hystérésis).
   *  previousType/newType : type de zone map_zones ('bubble' | 'meeting-room'), '' si aucune zone. */
  'bubble:changed': { previousBubbleId: string; previousType: string; newBubbleId: string; newType: string };
  /** Zones (map_zones) éditées — relais du canal Redis zones:{tenantId}:{mapId} via ZoneRoom */
  'colyseus:zones-changed': Record<string, never>;
  /** Murs (map_walls) édités — relais du canal Redis walls:{tenantId}:{mapId} via ZoneRoom */
  'colyseus:walls-changed': Record<string, never>;
  /** Sols (map_floor_layers) édités — relais du canal Redis floors:{tenantId}:{mapId} via ZoneRoom */
  'colyseus:floors-changed': Record<string, never>;
  /** Étiquettes (map_labels) éditées — relais du canal Redis labels:{tenantId}:{mapId} via ZoneRoom */
  'colyseus:labels-changed': Record<string, never>;
  /** Mobilier de la carte modifié dans l'éditeur (relais furniture:{tenantId}:{mapId}). */
  'colyseus:furniture-changed': Record<string, never>;
  /** Bureaux personnels de la carte modifiés (relais desks:{tenantId}:{mapId}). */
  'colyseus:desks-changed':  Record<string, never>;
  /** Connexion temps réel coupée sans que personne ne l'ait voulu (déploiement
   *  de campus-realtime, réseau, veille) — GameBridge relance la connexion.
   *  Jamais émis pour une expulsion, une session révoquée ou remplacée. */
  'colyseus:connection-lost': { code: number };

  // ── LiveKit → React / GameBridge ─────────────────────────
  'livekit:disconnected':     { roomType: 'zone' | 'broadcast' };
  'livekit:speakers-changed': { speakerUserIds: string[] };
  /** La room qui porte l'AV du joueur local a changé (zoneRoom ↔
   *  meetingRoomRoom, voir LiveKitService.localAvRoom/getActiveAvRoom) —
   *  signal pour les composants qui lisent l'AV active (VideoTiles) de
   *  re-résoudre getActiveAvRoom() et se ré-abonner à ses RoomEvent, sans
   *  quoi le swap de room reste invisible côté React (trouvé en review,
   *  2026-08-23 : VideoTiles restait bloqué sur zoneRoom pendant une
   *  réunion, aucune vidéo affichée pour personne). */
  'livekit:av-room-changed': Record<string, never>;
  /** Room broadcast prête à émettre le son (après connexion WebRTC établie) */
  'broadcast:ready':          {};

  // ── Colyseus → React (via GameBridge) ────────────────────
  'broadcast:started':        { adminUserId?: string };
  'broadcast:ended':          {};
  'moderation:kicked':        {};
  'moderation:muted':         { durationMs: number };
  /** Un admin a coupé le micro ou la caméra du joueur local (il peut les rallumer). */
  'moderation:media-off':     { kind: 'mic' | 'camera' };
  /** Fin du mute du joueur local (« Rendre la parole » ou durée écoulée). */
  'moderation:unmuted':       Record<string, never>;
  /** Session révoquée ailleurs (mot de passe changé dans une autre fenêtre) —
   *  déconnexion forcée, distincte d'un kick modération (voir ZoneRoom.disconnectRevokedSession). */
  'auth:session-revoked':     { reason?: SessionRevokedReason };
  /** Même compte connecté depuis une autre fenêtre ou un autre appareil — la
   *  dernière connexion l'emporte (ZoneRoom.replaceOlderSessions). Pas de
   *  déconnexion du compte : l'écran « Revenir ici » reprend la main. */
  'auth:session-replaced':    {};
  /** Carte désactivée par un admin pendant qu'on y est (ZoneRoom.closeZoneForMembers) :
   *  départ vers la carte d'arrivée. */
  'zone:closed':              {};
  /** Déconnexion demandée par l'utilisateur (menu de l'avatar) — révoque la
   *  session côté API (POST /auth/logout) puis déconnecte le client. */
  'auth:logout-requested':    {};
  'chat:proximity-received':  {
    /** Présent dans une salle de réunion (chat de la séance, ZoneRoom/meetingChat). */
    id?: string;
    senderId: string;
    displayName: string;
    text: string;
    timestamp: number;
    isSelf?: boolean;
  };
  /** Chat d'une salle de réunion à l'arrivée : messages de la séance, épingle, écriture réservée. */
  'meeting-chat:state':       {
    bubbleId: string;
    messages: Array<{ id: string; senderId: string; displayName: string; text: string; timestamp: number }>;
    pinnedId: string | null;
    locked: boolean;
  };
  'meeting-chat:settings':    { bubbleId: string; pinnedId: string | null; locked: boolean };
  /** Admin : épingler (null = retirer) un message du chat de réunion. */
  'ui:meeting-chat-pin':      { messageId: string | null };
  /** Admin : réserver (true) ou rouvrir l'écriture du chat de réunion. */
  'ui:meeting-chat-lock':     { locked: boolean };
  /** Un panneau latéral s'ouvre (un seul à la fois) : les autres se ferment. */
  'ui:side-panel-opened':     { panel: string };

  // ── Phaser → Colyseus (mouvement joueur local) ───────────
  /**
   * Émis par ZoneScene à chaque changement de position/animation.
   * Throttlé à ~20 FPS dans ZoneScene. ColyseusClient l'envoie au serveur.
   */
  'player:moved': {
    x: number; y: number;
    direction: 'up' | 'down' | 'left' | 'right';
    animation: 'idle' | 'walk';
  };

  // ── Modération → React ──────────────────────────────────────
  'moderation:player-muted':   { userId: string; durationMs?: number };
  'moderation:player-unmuted': { userId: string };

  // ── Wave ───────────────────────────────────────────────────
  'wave:received':             { fromUserId: string; fromDisplayName: string };
  /** Whiz reçu (POST /chat/dm/:userId/whiz, relayé par la ZoneRoom). */
  'whiz:received':             { fromUserId: string };

  // ── React → Phaser / Services ────────────────────────────
  'ui:toggle-mic':            {};
  'ui:toggle-camera':         {};
  'ui:chat-bubble-send':      { text: string };
  /** Overlay chat ouvert — Phaser doit suspendre le mouvement */
  'ui:chat-open':             {};
  /** Overlay chat fermé — Phaser peut reprendre le mouvement */
  'ui:chat-close':            {};
  /** Un modal React avec input est ouvert — Phaser doit libérer le clavier */
  'ui:modal-opened':          {};
  /** Le modal React est fermé — Phaser peut recapturer le clavier */
  'ui:modal-closed':          {};
  'ui:emote':                 { emoteId: string };
  'ui:status-change':         { status: 'available' | 'away' | 'busy' | 'focus' };
  'ui:focus-blocked':          { targetId: string; targetName: string };
  'ui:wave-send':             { targetUserId: string };
  'ui:toggle-screenshare':    {};
  /** Demande de changement de zone depuis le HUD */
  'zone:transition-requested': { targetSlug: string; spawnId: string };
  /** Colyseus → clients : état du verrou d'une bulle a changé */
  'bubble:lock-changed':      { bubbleId: string; locked: boolean; ownerUserId?: string; ownerUserIds?: string[] };
  // ── Tableaux blancs (Excalidraw) ──
  /** Ouvrir le tableau d'un objet posé (invite « Ouvrir le tableau »). */
  /** `claimId` : tableau d'un duo, en lecture seule (« Mes tableaux blancs »). */
  /** « Lire le tableau » : panneau d'affichage des duos (NoticeBoardModal). */
  'ui:notice-board-open':     { itemId: string };
  'ui:board-open':            { itemId: string; name: string; claimId?: string; deskArchive?: boolean };
  'ui:board-joined':          { itemId: string };
  'ui:board-left':            {};
  /** Éléments modifiés envoyés aux autres (seulement ceux qui ont changé). */
  'ui:board-update':          { itemId: string; elements: unknown[] };
  'ui:board-pointer':         { itemId: string; x: number; y: number; button: 'down' | 'up' };
  'board:updated':            { itemId: string; elements: unknown[] };
  'board:pointer':            { itemId: string; sessionId: string; userId: string; name: string; x: number; y: number; button: 'down' | 'up' };
  'board:pointer-left':       { itemId: string; sessionId: string };
  /** Tableau fermé par le serveur (partage retiré, sortie du bureau). */
  'board:closed':             { itemId: string };
  /** Le titulaire d'un bureau partage son tableau avec soi. */
  'board:shared':             { itemId: string; ownerUserId?: string; boardName?: string };
  'board:unshared':           { itemId: string };
  /** Colyseus → clients : un enregistrement a démarré/s'est arrêté pour une
   *  meeting-room (bandeau RGPD, spec design 2026-08-20) */
  'recording:changed':        { mapZoneId: string; recording: boolean; /** ISO */ startedAt?: string };
  /** Salles revendiquées par un duo sur la carte (état complet, ZoneRoom claims:state). */
  'claims:changed':           { claims: Array<{ id: string; zoneId: string; userIds: string[]; expiresAt: string }> };
  /** Client bloqué devant une bulle verrouillée → proposer de frapper */
  'ui:knock-prompt':          { bubbleId: string };
  /** Toast générique affiché en overlay */
  'ui:toast': { message: string; duration?: number; bypassFocus?: boolean; tone?: 'info' | 'success' | 'warning' | 'danger' };
  /** Toggle panneau bubble chat (proximité dans une bulle) */
  'ui:toggle-chat-panel':     {};
  /** Toggle panneau chat général (Entrée) */
  'ui:toggle-general-chat':   {};
  /** Profil mis à jour (nom/avatar) depuis un autre joueur via Colyseus */
  'player:profile-updated':   { sessionId: string; userId: string; displayName: string; avatarIndex: number };
  /** Appearance mise à jour depuis un autre joueur via Colyseus */
  'player:appearance-updated': { sessionId: string; userId: string; appearanceJson: string };
  /** UI → Colyseus : mettre à jour son propre profil */
  'ui:profile-update':        { displayName: string; avatarIndex: number };
  /** UI → Colyseus : mettre à jour son apparence composite */
  'ui:appearance-update':     { appearance: import('../types/index.js').AvatarAppearance };
  /** UI → Colyseus : verrouiller/déverrouiller une bulle */
  'ui:lock-bubble':           { bubbleId: string; locked: boolean };
  /** Client : bulle verrouillée, porte bloquée */
  'bubble:locked':            { bubbleId: string };
  /** UI → Colyseus : frapper à la porte d'une bulle verrouillée */
  'ui:knock-request':         { bubbleId: string };
  /** Colyseus → occupants : quelqu'un frappe */
  'knock:request':            { bubbleId: string; userId: string; displayName: string };
  /** UI → Colyseus : accepter/refuser un knock */
  'ui:knock-respond':         { userId: string; bubbleId: string; granted: boolean };
  /** Colyseus → demandeur : knock accepté */
  'knock:granted':            { bubbleId: string };
  /** Colyseus → demandeur : knock refusé */
  'knock:denied':             { bubbleId: string };

  // ── Portes ───────────────────────────────────────────────────
  /** Joueur local proche d'une porte — affiche l'invite DoorPrompt.
   *  Réémis si `locked` change pendant que le joueur est à proximité.
   *  `locked` = la touche ferait « toquer » au lieu d'ouvrir. */
  'door:nearby':              { doorId: string; doorName: string; linkedBubble: string; locked: boolean };
  /** Position écran (px, relative au canvas) du haut-centre de la porte proche —
   *  émise seulement quand elle change, uniquement tant qu'une porte est proche. */
  'door:prompt-position':     { x: number; y: number };
  /** Joueur local s'est éloigné de la porte */
  'door:left':                {};

  // ── Trottinettes (@campus/core/scooters, game/systems/Scooters.ts) ──
  /** Monter sur la trottinette à portée (ZoneScene → GameBridge → serveur). */
  'scooter:mount-request':    Pick<ScooterSnapshot, 'itemId' | 'model' | 'x' | 'y' | 'direction'>;
  /** Descendre de la trottinette. */
  'scooter:dismount-request': {};
  /**
   * Invite ScooterPrompt : monter (trottinette à portée) ou descendre (on roule) ;
   * sert aussi à l'assise : s'asseoir (siège libre à portée) ou se lever.
   */
  'scooter:prompt':           { mode: 'mount' | 'dismount' | 'sit' | 'stand' | 'board' | 'notice' };
  /** Position écran (px, relative au canvas) du point au-dessus duquel placer l'invite. */
  'scooter:prompt-position':  { x: number; y: number };
  /** Plus d'invite trottinette (rien à portée, ou une porte est plus proche). */
  'scooter:prompt-hidden':    {};

  // ── Ascenseur (@campus/core/elevators) ──
  /** Arrêté devant un ascenseur : fenêtre des étages (ZoneScene → ElevatorPanel). */
  'elevator:opened':          { currentSlug: string; floors: Array<{ mapSlug: string; floor: number; label: string; group: string }> };
  /** Plus devant l'ascenseur (ou étage choisi). */
  'elevator:closed':          {};
  /** Étage choisi dans la fenêtre. */
  'ui:elevator-choose':       { mapSlug: string };
  /** Fenêtre fermée sans choisir (Échap) : elle ne revient qu'après être reparti. */
  'ui:elevator-dismiss':      {};
  /** Entrer dans la cabine (ZoneScene → GameBridge → serveur, qui donne la place). */
  'elevator:board-request':   { zoneId: string; direction: 'up' | 'down' };
  /** Sortie de la cabine sur une place du palier, annoncée aux autres. */
  'elevator:arrive-request':  { zoneId: string; slot: number; x: number; y: number };

  // ── Assise (@campus/core/seats) ──
  /** S'asseoir sur le siège à portée (ZoneScene → GameBridge → serveur). */
  'seat:sit-request':         { x: number; y: number; direction: string; lift: number };
  /** Se lever. */
  'seat:stand-request':       {};

  // ── Zones area (iframe / launch) ──────────────────────────────
  /** Joueur local entre dans une zone area avec action iframe */
  'ui:area-entered':          { name: string; url: string; label: string };
  /** Joueur local sort d'une zone area */
  'ui:area-exited':           { name: string };
  /** Joueur local entre dans une zone area avec action launch */
  'ui:area-launch':           { name: string; url: string; label: string };

  // ── Réactions emoji ─────────────────────────────────────────
  /** UI → Colyseus : envoyer une réaction emoji */
  'ui:reaction-send':         { emoji: string };
  /** Colyseus → Phaser : réaction reçue d'un joueur */
  'reaction:received':        { userId: string; emoji: string; sessionId: string };

  // ── Zoom carte ──────────────────────────────────────────────
  /** UI → Phaser : changer le zoom de la caméra */
  'ui:zoom-change':           { level: number };

  // ── Périphériques audio/vidéo (Réglages) ─────────────────────
  /** UI → LiveKitService : appliquer un nouveau périphérique en direct (room déjà connectée) */
  'ui:device-changed':        { kind: 'audioinput' | 'videoinput' | 'audiooutput'; deviceId: string };

  // ── Contrôles tactiles (tap-to-move + bouton d'action) ───────
  /** Tap/drag sur la zone de jeu — coordonnées écran (clientX/clientY), converties
   *  en coordonnées monde par ZoneScene via la caméra. Émis au touchstart et en
   *  continu au touchmove (permet de "diriger" la destination en glissant). */
  'input:tap-move':           { clientX: number; clientY: number };
  /** Bouton d'action tactile pressé (équivalent Space/E) */
  'input:action-press':       {};

  // ── Messagerie (tasks/messagerie-plan.md) ─────────────────
  /** Message d'un autre reçu par la socket de la messagerie (`seen` : sa conversation est à l'écran). */
  'messaging:message-received': {
    conversationId: string;
    kind: 'general' | 'profile' | 'direct' | 'group';
    senderId: string;
    senderName: string;
    text: string;
    seen: boolean;
    /** Le message cite la personne (« @Prénom Nom », apps/web/src/messaging/mentions.ts). */
    mentionsMe: boolean;
  };

  /** Demande de connexion reçue, acceptée, refusée ou retirée (ConnectionService → ZoneRoom). */
  'connection:changed':       { type: 'requested' | 'accepted' | 'declined' | 'removed'; targetUserId: string; fromUserId: string };
  /** React : ouvrir / fermer le panneau Personnes (pastille « en ligne » du haut). */
  'ui:toggle-people':         {};
  /** Place prise par l'interface changée (panneau latéral ouvert/fermé, rangée de vignettes vidéo) : la caméra du jeu se recale. */
  'ui:hud-insets-changed':    Record<string, never>;
  /** React : ouvrir le chat sur une conversation privée (fiche de profil, notification). */
  'ui:open-dm':               { userId: string };

  // ── Transition inter-zones ─────────────────────────────────
  /** GameBridge → ZoneScene : relancer BootScene pour charger la nouvelle zone */
  'game:restart-scene':       {};

  // ── Bureau personnel (Sprint 11a) ──────────────────────────
  /** Joueur local entre dans une zone desk */
  'desk:entered':             { deskId: string; label: string; ownerId: string | null; ownerName: string | null };
  /** Joueur local sort d'une zone desk */
  'desk:exited':              {};
  /** UI → API : revendiquer ou libérer un bureau */
  'ui:desk-claim':            { deskId: string; zoneSlug: string };
  /** Bureaux personnels (PersonalDeskPrompt → PersonalDesks, game/systems). */
  'ui:personal-desk-place':   Record<string, never>;
  'ui:personal-desk-claim':   Record<string, never>;
  'ui:personal-desk-confirm': Record<string, never>;
  'ui:personal-desk-cancel':  Record<string, never>;
  'ui:personal-desk-release': Record<string, never>;
  'ui:personal-desk-customize':        Record<string, never>;
  'ui:personal-desk-customize-save':   Record<string, never>;
  'ui:personal-desk-customize-cancel': Record<string, never>;
  'ui:personal-desk-remove-carried':   Record<string, never>;
  'ui:personal-desk-select':           { furnitureId: string | null };
  /** Rouvrir la présentation des bureaux personnels (DeskTutorial). */
  'ui:desk-tutorial-open':             Record<string, never>;
  /** Fenêtre « Signaler / proposer » : capture du canvas (carte et avatars, sans les fenêtres ni les vidéos) + position. */
  /** Ouvrir la fenêtre « Signaler / proposer » sur un type (invitation FeedbackNudge). */
  'ui:feedback-open': { kind: 'bug' | 'idea' };
  'ui:feedback-capture': { reply: (r: { dataUrl: string | null; map?: string; tileX?: number; tileY?: number }) => void };
  'ui:desk-release':          { deskId: string; zoneSlug: string };
  /** Bureaux chargés depuis l'API */
  'desk:assignments-loaded':  { assignments: Array<{ deskId: string; userId: string; displayName: string }> };
  /** Quelqu'un arrive au bureau du joueur local */
  'desk:visitor-arrived':     { visitorName: string };

  // ── Présence par zone (Sprint 11b) ─────────────────────────
  /** Données de présence globale chargées */
  'presence:all-loaded':      { zones: Array<{ zoneSlug: string; zoneName: string; players: Array<{ userId: string; displayName: string; status: string }> }> };

  // ── Podium / speaker zone (Sprint 11d) ─────────────────────
  /** Joueur local entre dans une zone podium */
  'podium:entered':           { name: string };
  /** Joueur local sort de la zone podium */
  'podium:exited':            {};
  /** État du podium mis à jour par Colyseus */
  'podium:state':             { speakerId: string | null; speakerName: string | null };
  /** UI → Colyseus : lever la main */
  'ui:hand-raise':            {};
  /** UI → jeu : aller à son bureau personnel (même carte : à pied ; autre carte : changement de carte). */
  'ui:go-to-desk':            {};
  /** « Rejoindre » une personne : à pied jusqu'à elle, de carte en carte s'il le faut. */
  'ui:go-to-person':          { userId: string; zoneSlug: string; name: string };
  /** À pied jusqu'à un point d'une carte (salle d'une séance en cours). */
  'ui:go-to-place':           { zoneSlug: string; x: number; y: number; name: string };
  /** UI → Colyseus : accorder la parole */
  'ui:hand-grant':            { userId: string };
  /** Colyseus → UI : main levée par un joueur */
  'podium:hand-raised':       { userId: string; displayName: string };
  /** Colyseus → UI : audience doit couper le micro */
  'podium:mute-audience':     { speakerId: string };

  // ── Map Editor (Sprint ME) ────────────────────────────────
  /** EditorScene est prete */
  'editor:scene-ready':       {};
  /** React → EditorScene : changer de mode */
  'editor:set-mode':          { mode: 'select' | 'furniture' | 'zone' | 'wall' | 'door' | 'floor' | 'text' | 'slots' };
  /** React → EditorScene : zoom in/out depuis la toolbar */
  'editor:zoom':              { direction: 'in' | 'out' };
  /** React → EditorScene : selectionner un meuble du catalogue */
  'editor:select-furniture':  { id: string; url: string; layer: string; isSolid: boolean; solidBaseOnly: boolean } | null;
  /** EditorScene → React : meuble pose (pour rafraichir l'etat) */
  'editor:furniture-placed':  { id: string; furnitureId: string; tileX: number; tileY: number };
  /** EditorScene → React : meuble supprime */
  'editor:furniture-removed': { id: string };
  /** EditorScene → React : meuble posé sélectionné sur la carte (null = aucun), ou sa vue a changé */
  'editor:placed-furniture-selected': { id: string; furnitureId: string } | null;
  /** React → EditorScene : passer le meuble posé sélectionné à la vue suivante (1) ou précédente (-1), ou à cet id */
  'editor:rotate-placed-furniture': { step: 1 | -1 } | { furnitureId: string };
  /** React → EditorScene : type de zone selectionne (ou null pour desactiver) */
  'editor:select-zone-type':  { type: 'desk' | 'bubble' | 'door' | 'portal' | 'spawn' | 'meeting-room' | 'desk-area' | 'elevator' } | null;
  /** EditorScene → React : rectangle de zone dessine par l'utilisateur */
  'editor:zone-drawn':        { type: 'desk' | 'bubble' | 'door' | 'portal' | 'spawn' | 'meeting-room' | 'desk-area' | 'elevator'; x: number; y: number; width: number; height: number };
  /** React → EditorScene : zone creee en base, l'afficher */
  'editor:zone-created':      { id: string; type: string; name: string; properties: Record<string, unknown>; x: number; y: number; width: number; height: number };
  /** React → EditorScene : zone mise a jour, rafraichir l'affichage */
  'editor:zone-updated':      { id: string; type: string; name: string; properties: Record<string, unknown>; x: number; y: number; width: number; height: number };
  /** React → EditorScene : zone supprimee, retirer l'affichage */
  'editor:zone-deleted':      { id: string };
  'editor:zone-move-preview': { id: string; type: string; name: string; properties: Record<string, unknown>; x: number; y: number; width: number; height: number; revertX: number; revertY: number };
  'editor:zone-move-rejected': { id: string; x: number; y: number };
  /** EditorScene → React : zone existante cliquee */
  'editor:zone-selected':     { id: string; type: string; name: string; properties: Record<string, unknown>; x: number; y: number; width: number; height: number };
  /** EditorScene → React : zones chargees depuis la base */
  /** Bureaux personnels de la carte (lecture seule dans l'éditeur). */
  /** Emplacements de bureaux de la carte (tasks/emplacements-bureaux-plan.md). x/y relatifs à l'espace. */
  'editor:desk-slots':        { slots: Array<{ id: string; areaZoneId: string; x: number; y: number; tileX: number; tileY: number; width: number; height: number; outOfArea: boolean; deskId: string | null; ownerName: string | null }> };
  /** Mode « Emplacements » : ajouter (case vide d'un espace), déplacer (glisser), retirer (clic droit). */
  'editor:slot-add':          { areaZoneId: string; x: number; y: number };
  'editor:slot-move':         { id: string; x: number; y: number };
  'editor:slot-remove':       { id: string };
  /** Glisser un bureau : sur un emplacement libre (`slotId`), ou à une position libre de l'espace (x/y relatifs). */
  'editor:desk-move':         { id: string; slotId?: string; x?: number; y?: number };
  'editor:personal-desks':    { desks: Array<{ id: string; slotId: string | null; x: number; y: number; displayName: string; tileX: number; tileY: number; width: number; height: number; outOfArea: boolean; items: Array<{ furnitureId: string; dx: number; dy: number; layer: string }> }> };
  'editor:zones-loaded':      { zones: Array<{ id: string; type: string; name: string; properties: Record<string, unknown>; x: number; y: number; width: number; height: number }> };
  /** React → EditorScene : afficher/masquer la grille */
  'editor:toggle-grid':       { visible: boolean };
  /** React → EditorScene : selectionner un style de mur du catalogue (ou null pour desactiver) */
  'editor:select-wall-style': { id: string } | null;
  /** EditorScene → React : mur pose (pour rafraichir l'etat) */
  'editor:wall-placed':       { id: string; tileX: number; tileY: number; wallStyleId: string };
  /** EditorScene → React : mur supprime */
  'editor:wall-removed':      { id: string };
  /** React → EditorScene : selectionner une porte de DOOR_PALETTE (ou null pour desactiver) */
  'editor:select-door':       { id: string } | null;
  /** EditorScene → React : clic en mode Portes — React cree la zone porte via l'API */
  'editor:door-place':        { doorSprite: string; x: number; y: number; width: number };
  /** React → EditorScene : zone porte creee — retirer les murs sous la porte */
  'editor:door-placed':       { x: number; y: number; width: number };
  /** EditorScene → React : clic droit sur une porte en mode Portes — React la supprime via l'API */
  'editor:door-remove':       { id: string };
  /** React → EditorScene : réglages du mode Sols (couche, outil, matériau/terrain choisi) */
  'editor:floor-settings':    { layer: 'floor' | 'overlay'; tool: 'brush' | 'brush3' | 'rect' | 'fill' | 'eraser'; value: string | null };
  /** EditorScene → React : cases de sol modifiées et enregistrées (dirty tracking) */
  'editor:floor-painted':     { layer: 'floor' | 'overlay'; count: number };
  /** EditorScene → React : message à afficher (erreur d'enregistrement…) */
  'editor:toast':             { message: string };
  /** EditorScene → React : clic en mode Texte — sur une étiquette existante (id) ou une case vide */
  'editor:label-click':       { tileX: number; tileY: number; id: string | null };
  /** EditorScene → React : étiquette glissée à une nouvelle case (React enregistre via l'API) */
  'editor:label-moved':       { id: string; tileX: number; tileY: number };
  /** React → EditorScene : liste à jour des étiquettes de la carte (après chargement/modification) */
  'editor:labels-updated':    { labels: MapLabel[] };
};

class TypedEventBus extends Phaser.Events.EventEmitter {
  override emit<K extends keyof GameEventMap>(event: K, data: GameEventMap[K]): boolean {
    // DEBUG — log uniquement les events chat pour ne pas noyer la console
    if ((event as string).startsWith('chat:')) {
      console.log(`[EventBus] emit "${event as string}"`, data);
    }
    return super.emit(event as string, data);
  }
  override on<K extends keyof GameEventMap>(event: K, fn: (data: GameEventMap[K]) => void, context?: unknown): this {
    return super.on(event as string, fn, context);
  }
  override once<K extends keyof GameEventMap>(event: K, fn: (data: GameEventMap[K]) => void, context?: unknown): this {
    return super.once(event as string, fn, context);
  }
  override off<K extends keyof GameEventMap>(event: K, fn?: (data: GameEventMap[K]) => void, context?: unknown): this {
    return super.off(event as string, fn, context);
  }
}

export const EventBus = new TypedEventBus();
